<template>
  <div class="modelReg">
    <div class="modelReg__grid">
      <div class="modelReg__title">
        <h3>Сброс пароля</h3>
      </div>

      <div v-if="loading" class="modelReg__alertLoading"></div>
      <div v-if="errorMessage" class="modelReg__alertError">{{ errorMessage }}</div>
      <div v-if="successMessage" class="modelReg__alertSuccess">{{ successMessage }}</div>

      <!-- Информация о пользователе -->
      <div v-if="userInfo" class="user-info p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4">
        <div class="flex items-center gap-3">
          <UIcon name="i-heroicons-user" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <div>
            <div class="font-medium text-gray-900 dark:text-gray-100">{{ userInfo.first_name || 'Пользователь' }}</div>
            <div class="text-sm text-gray-500 dark:text-gray-400">{{ userInfo.default_phone_number }}</div>
          </div>
        </div>
      </div>

      <!-- Таймер токена -->
      <div v-if="tokenExpiry" class="token-timer p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mb-4">
        <div class="flex items-center gap-2">
          <UIcon name="i-heroicons-clock" class="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
          <span class="text-sm text-yellow-800 dark:text-yellow-200">
            Ссылка истекает через: {{ formatTimeLeft(tokenExpiry) }}
          </span>
        </div>
      </div>

      <UForm class="modelReg__form" :state="form" autocomplete="off">
        <UiBaseInputN
          v-model="form.password"
          type-input="password"
          label="Новый пароль"
          placeholder="Введите новый пароль"
          :error="v$.password.$error ? 'error' : ''"
          :errors="v$.password.$errors"
          :show-error="false"
          @change="v$.password.$touch"
        />

        <!-- Визуальная шкала силы пароля -->
        <PasswordStrengthMeter :password="form.password" :show-suggestions="false" />

        <!-- Требования к паролю -->
        <PasswordRequirementsList :validation="v$.password" />

        <UiBaseInputN
          v-model="form.confirmPassword"
          type-input="password"
          label="Подтвердите пароль"
          placeholder="Повторите новый пароль"
          :error="v$.confirmPassword.$error ? 'error' : ''"
          :errors="v$.confirmPassword.$errors"
          :show-error="true"
          @change="v$.confirmPassword.$touch"
        />

        <UiButtonMain
          class="btn"
          color="red"
          size="xl"
          label="Сбросить пароль"
          block
          type="submit"
          :disabled="v$.$invalid"
          @click.prevent="resetPassword"
        />

        <!-- Навигация -->
        <div class="text-center mt-4 space-y-2">
          <div>
            <UButton
              color="sky"
              variant="link"
              label="Вернуться назад"
              @click="modalStore.setState(modalStateEnum.ForgotPassword)"
            />
          </div>
        </div>
      </UForm>
    </div>
  </div>
</template>

<script setup lang="ts">
/* v8 ignore file */
import { consola } from 'consola'
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { useModalStore , modalStateEnum } from '@shared/model'

import APIAuth from '~/entities/auth'
import type { ResetPasswordDto } from '~/entities/auth'
import type { User } from '@entities/user'
import { useRulesForForm } from '@shared/lib'

const modalStore = useModalStore()
const { RulesForFormPassword, RulesForFormConfirmPassword } = useRulesForForm()

const logger = consola.withTag('ResetPassword')

// Состояние
const loading = ref(false)
const errorMessage = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const userInfo = ref<User | null>(null)
const tokenExpiry = ref<Date | null>(null)
const tokenTimer = ref<NodeJS.Timeout | null>(null)
const resetToken = ref<string>('')

// Форма (только пароли, без токена)
const form = reactive({
  password: '',
  confirmPassword: '',
})

// Валидация
const rules = computed(() => {
  return {
    password: RulesForFormPassword(),
    confirmPassword: RulesForFormConfirmPassword(form.password),
  }
})

const v$ = useVuelidate(rules, form)



// Принудительно обновляем валидацию confirmPassword при изменении password
watch(() => form.password, () => {
  if (v$.value.confirmPassword.$dirty) {
    v$.value.confirmPassword.$touch()
  }
})

// Методы
const resetPassword = async () => {
  v$.value.$validate()
  if (v$.value.$error || !resetToken.value) return

  loading.value = true
  errorMessage.value = null

  try {
    const payload: ResetPasswordDto = {
      token: resetToken.value,
      new_password: form.password,
      confirm_password: form.confirmPassword,
    }

    logger.log('🔄 Sending reset password request:', {
      token: resetToken.value ? 'present' : 'missing',
      new_password: form.password ? 'present' : 'missing',
      confirm_password: form.confirmPassword ? 'present' : 'missing',
    })

    const response = await APIAuth.resetPassword(payload)

    logger.log('✅ Password reset successful:', response)
    successMessage.value = 'Пароль успешно изменен'

    // Очищаем токен из localStorage после успешного сброса
    if (process.client) {
      localStorage.removeItem('resetPasswordToken')
    }

    // Переходим к логину через 2 секунды
    setTimeout(() => {
      modalStore.setState(modalStateEnum.Login)
    }, 2000)

  } catch (error: any) {
    logger.error('❌ Error resetting password:', error)
    logger.error('Error details:', error.response?.data || error.message)
    errorMessage.value = error?.data?.message || 'Ошибка при сбросе пароля'
  } finally {
    loading.value = false
  }
}



const formatTimeLeft = (expiry: Date) => {
  const now = new Date()
  const diff = expiry.getTime() - now.getTime()

  if (diff <= 0) {
    return 'Истек'
  }

  const minutes = Math.floor(diff / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const startTokenTimer = () => {
  if (tokenTimer.value) {
    clearInterval(tokenTimer.value)
  }

  tokenTimer.value = setInterval(() => {
    if (tokenExpiry.value) {
      const now = new Date()
      if (now >= tokenExpiry.value) {
        // Токен истек
        tokenExpiry.value = null
        userInfo.value = null
        resetToken.value = ''
        errorMessage.value = 'Ссылка для сброса пароля истекла'
        clearInterval(tokenTimer.value!)
      }
    }
  }, 1000)
}

// Очистка таймера при размонтировании
onUnmounted(() => {
  if (tokenTimer.value) {
    clearInterval(tokenTimer.value)
  }
})

// Загружаем данные из modalData (устанавливается middleware)
watch(() => modalStore.modalData, (newData) => {
  logger.log('🔍 ResetPassword: modalData changed', newData)

  if (newData.token) {
    logger.log('🎯 ResetPassword: Token found, setting up form')
    resetToken.value = newData.token

    // Сохраняем токен в localStorage для дополнительной безопасности
    if (process.client) {
      localStorage.setItem('resetPasswordToken', newData.token)
    }

    // Если есть данные пользователя, показываем их
    if (newData.user) {
      logger.log('👤 ResetPassword: User data available')
      userInfo.value = newData.user
      // Устанавливаем время истечения токена (15 минут от текущего времени)
      tokenExpiry.value = new Date(Date.now() + 15 * 60 * 1000)
      startTokenTimer()
    }
  }
}, { immediate: true })

// При загрузке компонента также проверяем localStorage
onMounted(() => {
  if (process.client) {
    const storedToken = localStorage.getItem('resetPasswordToken')
    if (storedToken && !resetToken.value) {
      logger.log('🔍 ResetPassword: Found token in localStorage')
      resetToken.value = storedToken
    }
  }
})
</script>

<style scoped lang="scss">
.user-info {
  @apply transition-all duration-200;
}

.token-timer {
  @apply transition-all duration-200;
}

.passwordRequirements {
  @apply space-y-2 mb-4 text-sm;
}
</style>

